import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  IconButton,
  TextField,
  Divider,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  RadioGroup,
  FormControlLabel,
  Radio,
} from '@mui/material';
import {
  Add,
  Remove,
  Delete,
  ShoppingCart,
  ArrowForward,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

interface CartItem {
  id: number;
  product_id: number;
  quantity: number;
  name: string;
  price: number;
  image_url: string;
}

interface Cart {
  id: number;
  items: CartItem[];
  total: number;
}

const Cart: React.FC = () => {
  const [cart, setCart] = useState<Cart | null>(null);
  const [loading, setLoading] = useState(true);
  const [couponCode, setCouponCode] = useState('');
  const [couponError, setCouponError] = useState('');
  const [couponSuccess, setCouponSuccess] = useState('');
  const [checkoutDialogOpen, setCheckoutDialogOpen] = useState(false);
  const [shippingAddress, setShippingAddress] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('card');
  const [shippingMethod, setShippingMethod] = useState('standard');
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    if (user) {
      fetchCart();
    } else {
      navigate('/login');
    }
  }, [user]);

  const fetchCart = async () => {
    try {
      setLoading(true);
      const response = await fetch('https://salon.rockyroadsolutions.com/api/cart', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        }
      });
      if (response.ok) {
        const data = await response.json();
        setCart(data);
      } else {
        console.error('Failed to fetch cart:', await response.text());
      }
    } catch (error) {
      console.error('Error fetching cart:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleQuantityChange = async (itemId: number, newQuantity: number) => {
    if (newQuantity < 1) return;

    try {
      const response = await fetch(`https://salon.rockyroadsolutions.com/api/cart/items/${itemId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({
          quantity: newQuantity,
        }),
      });

      if (response.ok) {
        fetchCart();
      } else {
        console.error('Failed to update quantity:', await response.text());
      }
    } catch (error) {
      console.error('Error updating quantity:', error);
    }
  };

  const handleRemoveItem = async (itemId: number) => {
    try {
      const response = await fetch(`https://salon.rockyroadsolutions.com/api/cart/items/${itemId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        }
      });

      if (response.ok) {
        fetchCart();
      } else {
        console.error('Failed to remove item:', await response.text());
      }
    } catch (error) {
      console.error('Error removing item:', error);
    }
  };

  const handleClearCart = async () => {
    try {
      const response = await fetch('https://salon.rockyroadsolutions.com/api/cart/clear', {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        }
      });

      if (response.ok) {
        fetchCart();
      } else {
        console.error('Failed to clear cart:', await response.text());
      }
    } catch (error) {
      console.error('Error clearing cart:', error);
    }
  };

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) {
      setCouponError('Please enter a coupon code');
      return;
    }

    try {
      const response = await fetch(`https://salon.rockyroadsolutions.com/api/coupons/validate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({
          code: couponCode,
          cart_total: cart?.total || 0,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setCouponSuccess(`Coupon applied! ${data.discount_type === 'percentage' ? data.discount_value + '%' : '$' + data.discount_value} off`);
        setCouponError('');
        fetchCart(); // Refetch cart with applied coupon
      } else {
        const errorData = await response.json();
        setCouponError(errorData.error || 'Invalid coupon code');
        setCouponSuccess('');
      }
    } catch (error) {
      console.error('Error applying coupon:', error);
      setCouponError('Error applying coupon');
      setCouponSuccess('');
    }
  };

  const handleCheckout = () => {
    if (!cart || cart.items.length === 0) return;
    setCheckoutDialogOpen(true);
  };

  const handlePlaceOrder = async () => {
    if (!shippingAddress) {
      return; // Show validation error
    }

    try {
      const response = await fetch('https://salon.rockyroadsolutions.com/api/orders', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({
          shipping_address: shippingAddress,
          payment_method: paymentMethod,
          shipping_method: shippingMethod,
          coupon_code: couponCode || undefined,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setCheckoutDialogOpen(false);
        navigate(`/orders/${data.order_id}`, { state: { success: true } });
      } else {
        console.error('Failed to place order:', await response.text());
      }
    } catch (error) {
      console.error('Error placing order:', error);
    }
  };

  if (loading) {
    return <Typography>Loading cart...</Typography>;
  }

  if (!cart || cart.items.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <ShoppingCart sx={{ fontSize: 60, color: '#E0E0E0', mb: 2 }} />
        <Typography variant="h5" gutterBottom>
          Your cart is empty
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
          Looks like you haven't added any products to your cart yet.
        </Typography>
        <Button
          variant="contained"
          onClick={() => navigate('/shop')}
          sx={{
            background: 'linear-gradient(135deg, #E91E63 0%, #AD1457 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, #AD1457 0%, #880E4F 100%)',
            },
          }}
        >
          Continue Shopping
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Shopping Cart
      </Typography>

      <Grid container spacing={4}>
        {/* Cart Items */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              {cart.items.map((item) => (
                <Box key={item.id} sx={{ mb: 3 }}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={3} sm={2}>
                      <Box
                        component="img"
                        src={item.image_url || 'https://via.placeholder.com/100x100?text=Product'}
                        alt={item.name}
                        sx={{ width: '100%', borderRadius: 1 }}
                      />
                    </Grid>
                    <Grid item xs={9} sm={4}>
                      <Typography variant="subtitle1" fontWeight={600}>
                        {item.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        ${typeof item.price === 'number' ? item.price.toFixed(2) : parseFloat(item.price).toFixed(2)}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <IconButton
                          size="small"
                          onClick={() => handleQuantityChange(item.id, item.quantity - 1)}
                        >
                          <Remove fontSize="small" />
                        </IconButton>
                        <TextField
                          size="small"
                          value={item.quantity}
                          onChange={(e) => {
                            const value = parseInt(e.target.value);
                            if (!isNaN(value) && value > 0) {
                              handleQuantityChange(item.id, value);
                            }
                          }}
                          inputProps={{ min: 1, style: { textAlign: 'center' } }}
                          sx={{ width: 60, mx: 1 }}
                        />
                        <IconButton
                          size="small"
                          onClick={() => handleQuantityChange(item.id, item.quantity + 1)}
                        >
                          <Add fontSize="small" />
                        </IconButton>
                      </Box>
                    </Grid>
                    <Grid item xs={4} sm={2} sx={{ textAlign: 'right' }}>
                      <Typography variant="subtitle1" fontWeight={600}>
                        ${((typeof item.price === 'number' ? item.price : parseFloat(item.price)) * item.quantity).toFixed(2)}
                      </Typography>
                    </Grid>
                    <Grid item xs={2} sm={1} sx={{ textAlign: 'right' }}>
                      <IconButton
                        color="error"
                        onClick={() => handleRemoveItem(item.id)}
                      >
                        <Delete />
                      </IconButton>
                    </Grid>
                  </Grid>
                  <Divider sx={{ mt: 2 }} />
                </Box>
              ))}

              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/shop')}
                >
                  Continue Shopping
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  onClick={handleClearCart}
                >
                  Clear Cart
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Order Summary */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Order Summary
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body1">Subtotal</Typography>
                <Typography variant="body1">${typeof cart.total === 'number' ? cart.total.toFixed(2) : parseFloat(cart.total).toFixed(2)}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body1">Shipping</Typography>
                <Typography variant="body1">Free</Typography>
              </Box>
              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                <Typography variant="h6">Total</Typography>
                <Typography variant="h6" color="#E91E63" fontWeight={700}>
                  ${typeof cart.total === 'number' ? cart.total.toFixed(2) : parseFloat(cart.total).toFixed(2)}
                </Typography>
              </Box>

              {/* Coupon Code */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Apply Coupon Code
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <TextField
                    size="small"
                    fullWidth
                    placeholder="Enter code"
                    value={couponCode}
                    onChange={(e) => setCouponCode(e.target.value)}
                  />
                  <Button
                    variant="outlined"
                    onClick={handleApplyCoupon}
                    sx={{ whiteSpace: 'nowrap' }}
                  >
                    Apply
                  </Button>
                </Box>
                {couponError && (
                  <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                    {couponError}
                  </Typography>
                )}
                {couponSuccess && (
                  <Typography variant="caption" color="success.main" sx={{ mt: 1, display: 'block' }}>
                    {couponSuccess}
                  </Typography>
                )}
              </Box>

              <Button
                variant="contained"
                fullWidth
                size="large"
                endIcon={<ArrowForward />}
                onClick={handleCheckout}
                sx={{
                  background: 'linear-gradient(135deg, #E91E63 0%, #AD1457 100%)',
                  py: 1.5,
                  '&:hover': {
                    background: 'linear-gradient(135deg, #AD1457 0%, #880E4F 100%)',
                  },
                }}
              >
                Proceed to Checkout
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Checkout Dialog */}
      <Dialog
        open={checkoutDialogOpen}
        onClose={() => setCheckoutDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Checkout</DialogTitle>
        <DialogContent>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Shipping Address
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={3}
                placeholder="Enter your complete shipping address"
                value={shippingAddress}
                onChange={(e) => setShippingAddress(e.target.value)}
                required
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography variant="h6" gutterBottom>
                Payment Method
              </Typography>
              <RadioGroup
                value={paymentMethod}
                onChange={(e) => setPaymentMethod(e.target.value)}
              >
                <FormControlLabel value="card" control={<Radio />} label="Credit/Debit Card" />
                <FormControlLabel value="paypal" control={<Radio />} label="PayPal" />
                <FormControlLabel value="cash" control={<Radio />} label="Cash on Delivery" />
              </RadioGroup>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography variant="h6" gutterBottom>
                Shipping Method
              </Typography>
              <FormControl fullWidth>
                <InputLabel>Shipping Method</InputLabel>
                <Select
                  value={shippingMethod}
                  onChange={(e) => setShippingMethod(e.target.value as string)}
                  label="Shipping Method"
                >
                  <MenuItem value="standard">Standard Shipping (Free)</MenuItem>
                  <MenuItem value="express">Express Shipping ($10.00)</MenuItem>
                  <MenuItem value="overnight">Overnight Shipping ($25.00)</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom>
                Order Summary
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography>Subtotal</Typography>
                <Typography>${typeof cart.total === 'number' ? cart.total.toFixed(2) : parseFloat(cart.total).toFixed(2)}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography>Shipping</Typography>
                <Typography>
                  {shippingMethod === 'standard'
                    ? 'Free'
                    : shippingMethod === 'express'
                    ? '$10.00'
                    : '$25.00'}
                </Typography>
              </Box>
              <Divider sx={{ my: 1 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="h6">Total</Typography>
                <Typography variant="h6" color="#E91E63" fontWeight={700}>
                  $
                  {(
                    parseFloat(typeof cart.total === 'number' ? cart.total.toString() : cart.total) +
                    (shippingMethod === 'standard'
                      ? 0
                      : shippingMethod === 'express'
                      ? 10
                      : 25)
                  ).toFixed(2)}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCheckoutDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handlePlaceOrder}
            sx={{
              background: 'linear-gradient(135deg, #E91E63 0%, #AD1457 100%)',
            }}
          >
            Place Order
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Cart;
